import { closestCenter, DndContext } from '@dnd-kit/core'
import type {
  CollisionDetection,
  DragEndEvent,
  DragMoveEvent,
} from '@dnd-kit/core'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react'

import { useDragSensors } from '@/components/SortableItem'

/** What a `DataTable` needs the drag context to call. */
export type TableDragHandlers = {
  collisionDetection: CollisionDetection
  onDragMove: (event: DragMoveEvent) => void
  onDragEnd: (event: DragEndEvent) => void
  onDragCancel: () => void
}

const AttachContext = createContext<
  ((handlers: TableDragHandlers | null) => void) | null
>(null)

/**
 * Stretches one table's drag over more of the screen than the table itself.
 *
 * A draggable belongs to the nearest `DndContext` above it, and a `DataTable`
 * makes its own — so a row picked up outside the table can never land in it.
 * Wrapping both in this makes the two one gesture: the source registers here,
 * the table attaches its handlers here, and the drop reaches the table's
 * `onMove` with the foreign key as the active one.
 *
 * The table inside keeps every rule — which zones a pair allows, what the
 * marker draws — so this adds no behaviour of its own. Without it a table is
 * exactly as it was.
 */
export function TableDragScope({ children }: { children: ReactNode }) {
  const handlers = useRef<TableDragHandlers | null>(null)
  const sensors = useDragSensors()

  const attach = useCallback((next: TableDragHandlers | null) => {
    handlers.current = next
  }, [])

  // Read through the ref at event time, so the context's props stay stable
  // while the table re-attaches a fresh closure on every render.
  const collisionDetection = useCallback<CollisionDetection>(
    args => handlers.current?.collisionDetection(args) ?? closestCenter(args),
    [],
  )
  const onDragMove = useCallback(
    (event: DragMoveEvent) => handlers.current?.onDragMove(event),
    [],
  )
  const onDragEnd = useCallback(
    (event: DragEndEvent) => handlers.current?.onDragEnd(event),
    [],
  )
  const onDragCancel = useCallback(() => handlers.current?.onDragCancel(), [])

  return (
    <AttachContext.Provider value={attach}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        {children}
      </DndContext>
    </AttachContext.Provider>
  )
}

/**
 * Registers this table's handlers with an enclosing scope, and says whether
 * there was one — a table without a scope makes its own `DndContext`.
 *
 * `null` for a table nothing can be dropped on: the scope routes one gesture,
 * so only the table that resolves drops may hold it, and a read-only list
 * rendered beside that one must not take the slot from it.
 */
export function useTableDragScope(handlers: TableDragHandlers | null): boolean {
  const attach = useContext(AttachContext)
  useEffect(() => {
    if (!handlers) return
    attach?.(handlers)
    return () => attach?.(null)
  }, [attach, handlers])
  return attach !== null && handlers !== null
}
